import { Box, Button, Typography, CircularProgress } from '@mui/material'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import Webcam from 'react-webcam'
import { styled } from '@mui/material/styles'
import { useUpdateKycMutation } from '~/app/store/services'
import { getBlob, visionAxios } from '~/utils'
import { cancelNarration, speak, t } from '~/utils/narration'
// import CircularProgressWithLabel from './styled/KycCircularProgress'
// import KycLinearProgress from './styled/KycLinearProgress'

const StyledImageBox = styled(Box)(({ theme }) => ({
  position: 'fixed',
  zIndex: 1,
  top: 0,
  left: 0,
  right: 0,
  bottom: '32%',
  height: '68%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#232323',
  padding: '48px 36px 60px 28px',
}))

const StyledWebcamBox = styled(Box)(({ theme }) => ({
  position: 'fixed',
  zIndex: 1,
  top: 0,
  left: 0,
  right: 0,
  bottom: '32%',
  height: '68%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}))

const StyledOverlayBox = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: '10%',
  right: '10%',
  bottom: '28%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2,
}))

const inputResolution = {
  facingMode: 'environment',
  width: { ideal: 2560 },
  height: { ideal: 1440 },
}

export default function VideoDoc({ onContinue, id }: any) {
  const webcamRef = useRef<Webcam>(null)
  // const mediaRecorderRef = useRef<MediaRecorder>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isRecording, setRecording] = useState<boolean>(false)
  const [screenshot, setScreenshot] = useState<string | null | undefined>(null)
  const [showScreenshot, setShowScreenshot] = useState(false) // Toggle to show screenshot or webcam view
  const [retakeAllowed, setRetakeAllowed] = useState(false) // Toggle to show screenshot or webcam view

  const [webcamKey, setWebcamKey] = useState(0)
  const [cameraError, setCameraError] = useState(false)
  const camAttempts = useRef(0)
  const onCameraError = (err: string | DOMException) => {
    console.error('camera failed to start', err)
    if (camAttempts.current < 4) {
      camAttempts.current += 1
      setTimeout(() => setWebcamKey((k) => k + 1), 700)
    } else setCameraError(true)
  }
  const retryCamera = () => {
    camAttempts.current = 0
    setCameraError(false)
    setWebcamKey((k) => k + 1)
  }

  const streamRef = useRef<MediaStream | null>(null)
  const enableAutoFocus = async () => {
    try {
      const track = streamRef.current?.getVideoTracks?.()[0]
      if (!track) return
      const caps = (track.getCapabilities?.() ?? {}) as any
      if (Array.isArray(caps.focusMode) && caps.focusMode.includes('continuous')) {
        await track.applyConstraints({ advanced: [{ focusMode: 'continuous' } as any] })
      }
    } catch (err) {
      console.warn('continuous focus not available on this device', err)
    }
  }

  const [updateKyc] = useUpdateKycMutation()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  useEffect(() => {
    if (id) updateKyc({ token, id, status: 'doc_page' })
  }, [])

  const checkValidDocument = async (image: string | null | undefined) => {
    if (image) {
      const payload = { input_image: image.split(',')[1] }
      const response = await visionAxios.post(`/validate_document`, payload)
      const data = response.data
      return data
    }
  }

  const checkBlurriness = async (image: string | null | undefined) => {
    if (image) {
      const payload = { input_image: image.split(',')[1] }
      const response = await visionAxios.post(`/docblur`, payload)
      const data = response.data
      console.log('document-blurr', data)
      return data
    }
  }

  const dataURLtoBlob = (dataurl: string): Blob => {
    const arr = dataurl.split(',')
    const mime = arr[0].match(/:(.*?);/)?.[1] || ''
    const bstr = atob(arr[1])
    let n = bstr.length
    const u8arr = new Uint8Array(n)

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n)
    }

    return new Blob([u8arr], { type: mime })
  }

  const handleCapture = async () => {
    try {
      const image = webcamRef.current?.getScreenshot()
      setScreenshot(image) // Save the screenshot

      if (!image) throw new Error('Failed to capture image')
      // setIsLoading(true)

      // After validation, show the screenshot
      setShowScreenshot(true) // Show the captured image
      setRetakeAllowed(true) // Allow user to retake the image
    } catch (error) {
      enqueueSnackbar(t({ en: error.message, hi: 'तस्वीर लेने में विफल। कृपया पुनः प्रयास करें।' }), {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      })
    } finally {
      // setIsLoading(false)
      // setRecording(false)
    }
  }

  const handleRetakeButtonClick = () => {
    setShowScreenshot(false) // Switch back to webcam for retake
    setRetakeAllowed(false) // Allow user to retake the image
  }

  const handleContinueWithScreenshot = async () => {
    try {
      if (!screenshot) throw new Error('Failed to capture image')
      setIsLoading(true)

      const retake = (msg: string) => {
        enqueueSnackbar(msg, {
          variant: 'error',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
        })
        speak(msg)
        setScreenshot(null)
        setShowScreenshot(false)
        setRetakeAllowed(false)
      }

      const checkBlur = await checkBlurriness(screenshot)
      if (checkBlur && checkBlur.blurry === 'Yes') {
        retake(
          t({
            en: 'The document image is blurry. Please capture it again in focus.',
            hi: 'डॉक्यूमेंट की तस्वीर धुंधली है। कृपया इसे फ़ोकस में फिर से लें।',
          })
        )
        return
      }

      const checkValidDoc = await checkValidDocument(screenshot) // checkValidDoc = {confidence, document_type, message, status}
      console.log(checkValidDoc)
      if (checkValidDoc.document_type.trim() === 'UNKNOWN') {
        retake(
          t({
            en: 'Unable to verify the document. Please capture it again.',
            hi: 'डॉक्यूमेंट वेरिफ़ाई नहीं हो सका। कृपया इसे फिर से लें।',
          })
        )
      } else {
        setRecording(true)

        const config = { headers: { 'Content-Type': 'multipart/form-data', id } }

        // const formData = new FormData()
        // const blob = dataURLtoBlob(screenshot || '')
        // const file = new File([blob], 'cus_video_5sec')
        // formData.append('doc_image', file, 'cus_image.jpg')
        // await pyAxios.post(`/document`, formData, config)

        await updateKyc({ image: screenshot, id, status: 'doc_uploaded' })

        onContinue()
      }
    } catch (error) {
      console.log('Document processing error:', error.message)
      enqueueSnackbar(t({ en: 'Something went wrong! Please try after sometime.', hi: 'कुछ गड़बड़ हो गई! कृपया कुछ समय बाद प्रयास करें।' }), {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      })
    } finally {
      setIsLoading(false)
      setRecording(false)
    }
  }

  // const handleCapture = async () => {
  //   // const recType = 'document'
  //   try {
  //     const image = webcamRef.current?.getScreenshot()
  //     setScreenshot(image)
  //     const _image = await getBlob(webcamRef.current)

  //     if (!image) throw new Error('Failed to capture image')
  //     setIsLoading(true)

  //     const checkValidDoc = await checkValidDocument(image) // checkValidDoc = {confidence, document_type, message, status}
  //     await checkBlurriness(image) // api response fix from vision side required
  //     console.log(checkValidDoc)
  //     if (checkValidDoc.document_type === 'UNKNOWN') {
  //       // window.alert(checkValidDoc.message)
  //       enqueueSnackbar(checkValidDoc.message, {
  //         variant: 'error',
  //         anchorOrigin: { vertical: 'top', horizontal: 'center' },
  //       })
  //     } else {
  //       setRecording(true)

  //       const config = { headers: { 'Content-Type': 'multipart/form-data', id } }

  //       const formData = new FormData()
  //       const file = new File([_image], 'cus_video_5sec')
  //       formData.append('doc_image', file, 'cus_image.jpg')

  //       await pyAxios.post(`/document`, formData, config)

  //       // await handleStartRecording(webcamRef, mediaRecorderRef, setRecording, id, recType, image)
  //       await updateKyc({ image, id, status: 'doc_uploaded' })

  //       onContinue()
  //     }
  //   } catch (error) {
  //     console.log(error)
  //   } finally {
  //     setIsLoading(false)
  //     setRecording(false)
  //   }
  // }

  useEffect(() => {
    speak({
      en: 'keep your hands steady during this process',
      hi: 'इस प्रक्रिया के दौरान अपने हाथ स्थिर रखें',
    })
    return () => cancelNarration()
  }, [])

  return (
    <>
      {showScreenshot && screenshot ? (
        <StyledImageBox>
          <img
            src={screenshot}
            alt="Captured Document"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              border: '6px solid #FFF',
              borderRadius: 4,
            }}
          />
          {isLoading && !isRecording && (
            <img
              style={{ height: '70%', maxWidth: '80%', objectFit: 'contain', position: 'absolute' }}
              src="/images/scanning.gif"
            />
          )}
        </StyledImageBox>
      ) : (
        <>
          <StyledWebcamBox onClick={enableAutoFocus}>
            <Webcam
              key={webcamKey}
              ref={webcamRef}
              videoConstraints={inputResolution}
              forceScreenshotSourceSize
              screenshotQuality={0.92}
              imageSmoothing={false}
              screenshotFormat="image/jpeg"
              onUserMedia={(stream) => {
                camAttempts.current = 0
                setCameraError(false)
                streamRef.current = stream
                enableAutoFocus()
              }}
              onUserMediaError={onCameraError}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            {cameraError && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 5,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  px: 3,
                  textAlign: 'center',
                  color: '#fff',
                }}
              >
                <Typography variant="subtitle1">
                  {t({
                    en: 'Couldn’t start the camera. Make sure no other app is using it, then try again.',
                    hi: 'कैमरा शुरू नहीं हो सका। सुनिश्चित करें कि कोई अन्य ऐप इसका उपयोग नहीं कर रहा है, फिर पुनः प्रयास करें।',
                  })}
                </Typography>
                <Button variant="contained" onClick={retryCamera}>
                  {t({ en: 'Retry camera', hi: 'कैमरा पुनः आज़माएँ' })}
                </Button>
              </Box>
            )}
          </StyledWebcamBox>
          <StyledOverlayBox>
            <img
              style={{ height: '70%', maxWidth: '100%', objectFit: 'contain' }}
              src="/images/document_border.png"
            />
          </StyledOverlayBox>
        </>
      )}
      <Box
        display="flex"
        flexDirection="column"
        position="fixed"
        bottom="0%"
        height="35%"
        width="100%"
        left={0}
        alignItems="center"
        zIndex={2}
        borderRadius="30px 30px 0 0"
        padding="16px 24px"
        bgcolor="#FFF"
      >
        {isLoading ? (
          <>
            <Box sx={{ width: '100%' }}>
              {/* <Typography variant='h6' fontWeight={600} py={1} borderBottom="1px solid rgba(0,0,0,0.0)">Saving, please wait...</Typography> */}
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {isRecording
                  ? t({ en: 'Uploading document, please wait...', hi: 'डॉक्यूमेंट अपलोड हो रहा है, कृपया प्रतीक्षा करें...' })
                  : t({ en: 'Analysing document, please wait...', hi: 'डॉक्यूमेंट का विश्लेषण हो रहा है, कृपया प्रतीक्षा करें...' })}
              </Typography>
              <Typography variant="subtitle1">
                {t({
                  en: 'Please avoid hitting the back button or refreshing the page during this process.',
                  hi: 'इस प्रक्रिया के दौरान कृपया बैक बटन न दबाएँ या पेज रीफ़्रेश न करें।',
                })}
              </Typography>

              <CircularProgress sx={{ marginTop: 6 }} />
              {/* {isRecording ? <KycCircularProgress /> : <CircularProgress sx={{ marginTop: 6 }} />} */}
            </Box>
          </>
        ) : (
          <>
            {retakeAllowed ? (
              <>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {t({ en: 'Would you like to continue with this document photo?', hi: 'क्या आप इस डॉक्यूमेंट फ़ोटो के साथ आगे बढ़ना चाहेंगे?' })}
                </Typography>
                <Box flex={1} />
                <Box
                  position="absolute"
                  bottom={0}
                  width="100%"
                  padding={2}
                  borderTop="1px solid #E0E0E0"
                >
                  <Button
                    size="large"
                    variant="outlined"
                    fullWidth
                    sx={{ py: 1.5, mb: 1 }}
                    disabled={isLoading}
                    onClick={handleRetakeButtonClick}
                  >
                    {t({ en: 'Re-capture', hi: 'फिर से लें' })}
                  </Button>
                  <Button
                    size="large"
                    fullWidth
                    sx={{ py: 1.5 }}
                    disabled={isLoading}
                    onClick={handleContinueWithScreenshot}
                  >
                    {t({ en: 'Yes, continue', hi: 'हाँ, आगे बढ़ें' })}
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {t({ en: 'Next, we will capture a photo of your ID document', hi: 'अब हम आपके पहचान डॉक्यूमेंट की फ़ोटो लेंगे' })}
                </Typography>

                <Typography>
                  {t({
                    en: 'Place your ID document inside bounding box. Keep your hands steady during this process.',
                    hi: 'अपना पहचान डॉक्यूमेंट बॉक्स के अंदर रखें। इस प्रक्रिया के दौरान अपने हाथ स्थिर रखें।',
                  })}
                </Typography>
                <Box flex={1} />
                <Box
                  position="absolute"
                  bottom={0}
                  width="100%"
                  padding={2}
                  borderTop="1px solid #E0E0E0"
                >
                  <Button
                    size="large"
                    fullWidth
                    sx={{ py: 1.5 }}
                    disabled={isLoading}
                    onClick={handleCapture}
                  >
                    {t({ en: 'Capture document', hi: 'डॉक्यूमेंट की फ़ोटो लें' })}
                  </Button>
                </Box>
              </>
            )}
          </>
        )}
      </Box>
    </>
  )
}
